import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, GlobalCollectionId, GlobalDataSourceId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, ResolvedMonospaceCollection, ResolvedMonospaceDataSource, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { WorkspaceEntityStore } from '#layers/runtime/public';
import { normalizeCollectionRelations, resolveCollectionOppositeRelations } from '@rstore/core';
import { addCollection, removeCollection } from '@rstore/vue';
import { defineStore } from 'pinia';
import { computed, toValue } from 'vue';
import { toStoreCollection } from '#layers/data-model/public';
import { defineWorkspaceHydration, workspaceRuntime } from '#layers/runtime/public';
import { getWorkspaceSchemaOverrides } from '#layers/workspace/rstore/overrides/apply-overrides';

interface HydrateWorkspaceSchemaOptions {
	sources: ResolvedMonospaceDataSource[];
	store: WorkspaceEntityStore;
}

const DATA_SOURCE_INCLUDE = {
	namespaces: {},
	collections: {
		// TODO expose the maximum allowed limit by the engine (set as query_limit_max in config)
		// TODO pagination? count?
		limit: 1000,
		include: {
			primitiveFields: {
				include: {
					meta: {},
				},
			},
			relationFields: {
				include: {
					constraint: {
						include: {
							fieldMappings: {},
						},
					},
					meta: {},
				},
			},
			indexes: {
				include: {
					fields: {},
				},
			},
			meta: {},
		},
	},
} as const;

/** The data sources of a workspace runtime, deep enough to register its collections. */
export const workspaceDataSourcesHydration = defineWorkspaceHydration((store) => store.MonospaceDataSource.query((q) => q.many({
	include: DATA_SOURCE_INCLUDE,
})), { empty: [] });

/** Registers the loaded schema on its store: schema overrides, then one rstore collection per workspace collection. */
export function hydrateWorkspaceSchema({ sources, store }: HydrateWorkspaceSchemaOptions) {
	const collections = sources.flatMap((source) => source.collections);
	getWorkspaceSchemaOverrides(store).apply(sources);
	cleanupWorkspaceSchema({ currentCollections: collections, store });
	const collectionsByName = new Map(store.$collections.map((collection) => [collection.name, collection]));

	let patchedRelations = false;

	for (const monospaceCollection of collections) {
		const storeCollection = toStoreCollection(monospaceCollection);
		const existingCollection = collectionsByName.get(monospaceCollection.apiName);

		if (existingCollection) {
			existingCollection.meta ??= {};

			if (!monospaceCollection.isSystem) {
				const { getKey } = storeCollection;

				existingCollection.fields = storeCollection.fields;
				Object.assign(existingCollection.meta, storeCollection.meta);
				existingCollection.relations = storeCollection.relations;
				normalizeCollectionRelations([existingCollection]);
				existingCollection.scopeId = storeCollection.scopeId;

				if (getKey) {
					existingCollection.getKey = (item: AnyItem) => item.$overrideKey ?? getKey(item);
				}

				patchedRelations = true;
			}
			else {
				Object.assign(existingCollection.meta, {
					relationsWithConstraint: storeCollection.meta.relationsWithConstraint,
				});
			}

			continue;
		}

		addCollection(store, storeCollection);
	}

	if (patchedRelations) resolveCollectionOppositeRelations(store.$collections);
}

function cleanupWorkspaceSchema({
	currentCollections,
	store,
}: { currentCollections: ResolvedMonospaceCollection[]; store: WorkspaceEntityStore }) {
	const collectionNames = new Set(currentCollections.map((collection) => collection.apiName));

	for (const collection of store.$collections) {
		if (collection.meta?.fromWorkspaceCollections && !collectionNames.has(collection.name)) {
			removeCollection(store, collection.name);
		}
	}
}

export const useWorkspaceSchemaStore = defineStore('workspace-schema', () => {
	const { data: dataSources, isRefreshing, refresh: refreshDataSources } = workspaceDataSourcesHydration.use();

	const allCollections = computed(() => dataSources.value.flatMap((source) => source.collections));
	const allPrimitiveFields = computed(() => allCollections.value.flatMap((collection) => collection.primitiveFields));
	const allSingleRelationFields = computed(() => allCollections.value.flatMap((collection) => collection.relationFields));

	const collectionsById = computed(() => Object.fromEntries(allCollections.value.map((collection) => [collection.id, collection])));
	const primitiveFieldsById = computed(() => Object.fromEntries(allPrimitiveFields.value.map((field) => [field.id, field])));
	const singleRelationFieldsById = computed(() => Object.fromEntries(allSingleRelationFields.value.map((field) => [field.id, field])));

	return {
		dataSources,
		collections: allCollections,
		nonSystemCollections: computed(() => allCollections.value.filter(({ isSystem }) => !isSystem)),
		primitiveFields: allPrimitiveFields,

		getCollection,
		findFieldById,
		findFieldByName,

		findSourceById,
		findCollectionById,
		findPrimitiveFieldById,
		findPrimitiveFieldByName,
		findCollectionByName,
		findSingleRelationFieldById,
		findSingleRelationFieldByName,

		isRefreshing,
		refresh,
	};

	/** Reloads the active runtime's schema; a runtime displaced mid-flight keeps its registrations untouched. */
	async function refresh() {
		const runtime = workspaceRuntime.activeRuntime.value;
		if (!runtime) return;

		await refreshDataSources();
		if (workspaceRuntime.activeRuntime.value !== runtime) return;
		hydrateWorkspaceSchema({ sources: dataSources.value, store: runtime.store });
	}

	function findSourceById(sourceId: GlobalDataSourceId): ResolvedMonospaceDataSource | undefined {
		return dataSources.value.find(({ id }) => id === sourceId);
	}

	function findCollectionById(collectionId: GlobalCollectionId): ResolvedMonospaceCollection | undefined {
		return collectionsById.value[collectionId];
	}

	function findCollectionByName(collectionName: string): ResolvedMonospaceCollection | undefined {
		return allCollections.value.find((collection) => collection.apiName === collectionName);
	}

	function findPrimitiveFieldById(fieldId: GlobalPrimitiveFieldId): ResolvedMonospacePrimitiveField | undefined {
		return primitiveFieldsById.value[fieldId];
	}

	function findPrimitiveFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string): ResolvedMonospacePrimitiveField | undefined {
		return toValue(allPrimitiveFields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function findFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string) {
		const relationField = findSingleRelationFieldByName(collection, fieldName);
		const primitiveField = findPrimitiveFieldByName(collection, fieldName);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldById(fieldId: GlobalSingleRelationFieldId): ResolvedMonospaceSingleRelationField | undefined {
		return singleRelationFieldsById.value[fieldId];
	}

	function findFieldById(fieldId: GlobalFieldId) {
		const primitiveField = findPrimitiveFieldById(fieldId as GlobalPrimitiveFieldId);
		const relationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);
		return relationField ?? primitiveField;
	}

	function findSingleRelationFieldByName(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>, fieldName: string, fields: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField[]> = allSingleRelationFields): ResolvedMonospaceSingleRelationField | undefined {
		return toValue(fields).find((field) => {
			return field.apiName === fieldName && field.collection?.id === toValue(collection).id;
		});
	}

	function getCollection(collectionOrId: GlobalCollectionId | ResolvedMonospaceCollection): ResolvedMonospaceCollection | undefined {
		return typeof collectionOrId === 'string' ? findCollectionById(collectionOrId) : collectionOrId;
	}
});
